import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Inbox, CalendarClock, MessageCircle, FileClock, UserPlus, HardHat, Wallet, PhoneCall } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtEuro, greeting } from "@/lib/format";
import { Btn, Modal } from "@/components/rp";
import { NewClientDialog } from "@/components/admin/dialogs";

const StatCard = ({ icon: Icon, label, value, onClick, id }) => (
  <button onClick={onClick} data-testid={`admin-stat-card-${id}`} className="rp-card p-5 text-left hover:bg-[#E6DDCE]/50 hover:border-[#C8BDAB] transition-colors">
    <Icon className="h-5 w-5 text-[#757575]" strokeWidth={1.5} />
    <p className="text-3xl font-semibold tracking-tight mt-4" data-testid={`admin-stat-value-${id}`}>{value ?? "–"}</p>
    <p className="text-xs text-[#757575] mt-1">{label}</p>
  </button>
);

const PendingQuotes = ({ open, onClose }) => {
  const [docs, setDocs] = useState([]);
  useEffect(() => { if (open) api.get("/documents").then((r) => setDocs(r.data.filter((d) => d.category === "preventivo" && d.status === "pending"))); }, [open]);
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title="Preventivi in attesa" testid="pending-quotes-dialog">
      <div className="space-y-2">
        {!docs.length && <p className="text-sm text-[#757575]">Nessun preventivo in attesa.</p>}
        {docs.map((d) => (
          <Link key={d.id} to={`/admin/cantieri/${d.cantiere_id}`} className="block rounded-2xl border border-[#EBEBEB] p-4 hover:bg-[#F7F7F7]">
            <p className="font-medium">{d.title}</p>
            <p className="text-xs text-[#757575]">{fmtEuro(d.amount)}</p>
          </Link>
        ))}
      </div>
    </Modal>
  );
};

export const AdminOverview = ({ onPayments }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [newClient, setNewClient] = useState(false);
  const [quotes, setQuotes] = useState(false);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  const STATS = [
    { id: "open-orders", icon: HardHat, label: "Cantieri aperti", value: stats.open_orders, onClick: () => document.getElementById("cantieri-list-anchor")?.scrollIntoView({ behavior: "smooth" }) },
    { id: "clients", icon: Users, label: "Clienti", value: stats.clients, onClick: () => navigate("/admin/clienti") },
    { id: "new-leads", icon: Inbox, label: "Nuove richieste", value: stats.new_leads, onClick: () => navigate("/admin/agenda?tab=leads") },
    { id: "pending-appointments", icon: CalendarClock, label: "Appuntamenti da confermare", value: stats.pending_appointments, onClick: () => navigate("/admin/agenda") },
    { id: "unread-messages", icon: MessageCircle, label: "Messaggi non letti", value: stats.unread_messages, onClick: () => navigate("/admin/messaggi") },
    { id: "pending-quotes", icon: FileClock, label: "Preventivi in attesa", value: stats.pending_quotes, onClick: () => setQuotes(true) },
    { id: "payments-to-register", icon: Wallet, label: "Saldi da registrare", value: stats.payments_to_register, onClick: onPayments },
    { id: "quotes-to-recall", icon: PhoneCall, label: "Preventivi da richiamare", value: stats.quotes_to_recall, onClick: () => navigate("/admin/preventivi?status=da_richiamare") },
  ];

  return (
    <div className="mb-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 rp-fade">
        <div>
          <p className="rp-label mb-2">Area staff</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="admin-greeting">{greeting()}, {user.name.split(" ")[0]}</h1>
        </div>
        <Btn onClick={() => setNewClient(true)} data-testid="admin-new-client-button"><UserPlus className="h-4 w-4" /> Nuovo cliente</Btn>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{STATS.map((s) => <StatCard key={s.id} {...s} />)}</div>
      <NewClientDialog open={newClient} onClose={() => setNewClient(false)} onSaved={(r) => navigate(r.cantiere_id ? `/admin/cantieri/${r.cantiere_id}` : `/admin/clienti/${r.client.id}`)} />
      <PendingQuotes open={quotes} onClose={() => setQuotes(false)} />
    </div>
  );
};
